import { router } from "@inertiajs/react";
import { LoaderIcon } from "lucide-react";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { getCsrfToken } from "@/lib/csrf";

export type DeletableDocument = "soap" | "evidence";

export interface DeleteDocumentTarget {
	appointmentId: string;
	kind: DeletableDocument;
	// How the admin recognises the visit. The Orders menu shows nothing of the document
	// itself, so this is all they confirm against there.
	visitNumber: number;
	totalVisits: number;
	therapistName?: string | null;
	// Whether the visit also holds the other document, which the dialog then offers to
	// delete in the same go.
	hasOtherDocument?: boolean;
}

// `gone` marks a 404: the document was already deleted elsewhere.
class DeleteError extends Error {
	constructor(
		message: string,
		readonly gone: boolean,
	) {
		super(message);
	}
}

const OTHER_DOCUMENT: Record<DeletableDocument, DeletableDocument> = {
	soap: "evidence",
	evidence: "soap",
};

// Confirms and runs a SOAP or evidence deletion. Upstream only soft-deletes, but nothing
// in the portal can bring the document back, so it is always asked for first.
export function DeleteDocumentDialog({
	target,
	onClose,
	reloadOnly,
}: {
	target: DeleteDocumentTarget | null;
	onClose: () => void;
	reloadOnly: string[];
}) {
	const { t } = useTranslation("appointments");
	const [isDeleting, setIsDeleting] = useState(false);
	const [alsoOther, setAlsoOther] = useState(false);
	// The content stays on screen through the close animation after `target` goes null;
	// rendering from the last target keeps it from flashing the SOAP wording and blanks.
	const lastTarget = useRef(target);
	if (target) lastTarget.current = target;
	const shown = target ?? lastTarget.current;
	const kind = shown?.kind ?? "soap";
	const other = OTHER_DOCUMENT[kind];

	const close = () => {
		setAlsoOther(false);
		onClose();
	};

	const deleteDocument = async (
		appointmentId: string,
		doc: DeletableDocument,
	) => {
		const csrfToken = getCsrfToken();
		const response = await fetch(
			`/api/v1/appointments/${appointmentId}/${doc}`,
			{
				method: "DELETE",
				headers: {
					Accept: "application/json",
					"X-CSRF-Token": csrfToken,
				},
			},
		);
		const data = await response.json().catch(() => ({}));
		if (!response.ok || !data.success) {
			throw new DeleteError(
				data.error || t(`form.delete_document.${doc}.error`),
				response.status === 404,
			);
		}
	};

	// One request per document, each leaving its own timeline row. If the second one
	// fails the first has still landed, so the page is reloaded either way — and so it is
	// on a 404, where the document is already gone and the row on screen is stale.
	const onConfirm = async () => {
		if (!target) return;
		const docs = [
			kind,
			...(alsoOther && target.hasOtherDocument ? [other] : []),
		];
		let changed = false;
		setIsDeleting(true);
		for (const doc of docs) {
			try {
				await deleteDocument(target.appointmentId, doc);
				changed = true;
				toast.success(t(`form.delete_document.${doc}.success`));
			} catch (err: unknown) {
				if (err instanceof DeleteError && err.gone) changed = true;
				toast.error(
					err instanceof Error
						? err.message
						: t(`form.delete_document.${doc}.error`),
				);
				break;
			}
		}
		setIsDeleting(false);
		if (changed) {
			close();
			router.reload({ only: reloadOnly });
		}
	};

	return (
		<AlertDialog
			open={!!target}
			onOpenChange={(open) => {
				if (!open && !isDeleting) close();
			}}
		>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						{t(`form.delete_document.${kind}.title`)}
					</AlertDialogTitle>
					<AlertDialogDescription className="space-y-2">
						<span className="block">
							<Trans
								i18nKey={
									shown?.therapistName
										? "form.delete_document.description"
										: "form.delete_document.description_no_therapist"
								}
								ns="appointments"
								values={{
									number: shown?.visitNumber,
									total: shown?.totalVisits,
									therapist: shown?.therapistName,
								}}
								components={{
									b: <b className="font-semibold text-foreground" />,
								}}
								// Values are escaped before the <b> markup is parsed, so a name
								// cannot add tags or attributes, then unescaped inside the text
								// so a therapist's "&" or a "/" does not print as an entity.
								// The app turns escaping off globally, so it is asked for here.
								tOptions={{ interpolation: { escapeValue: true } }}
								shouldUnescape
							/>
						</span>
						<span className="block">{t("form.delete_document.unlock")}</span>
					</AlertDialogDescription>
				</AlertDialogHeader>
				{shown?.hasOtherDocument && (
					<div className="flex items-center justify-between gap-3 rounded-md border p-3">
						<Label htmlFor="delete-other-document" className="font-normal">
							{t(`form.delete_document.${kind}.also_other`)}
						</Label>
						<Switch
							id="delete-other-document"
							checked={alsoOther}
							onCheckedChange={setAlsoOther}
							disabled={isDeleting}
						/>
					</div>
				)}
				<AlertDialogFooter>
					<AlertDialogCancel disabled={isDeleting}>
						{t("form.delete_document.cancel")}
					</AlertDialogCancel>
					<AlertDialogAction
						className={buttonVariants({ variant: "destructive" })}
						disabled={isDeleting}
						onClick={(e) => {
							// Radix closes on click; stay open until the request settles.
							e.preventDefault();
							onConfirm();
						}}
					>
						{isDeleting && <LoaderIcon className="animate-spin" />}
						{t("form.delete_document.submit")}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
